import { get } from 'lodash'
import { translateKey } from 'GlobalStores/Cores/Translator/useTranslate'
import { uaeAreaCountries } from './UAEHelpers'

// The address endpoint reports every problem it found as a list of dictionary keys in `Message_v2`.
// That list is the only place that says which field the BE rejected, so it has to win over the FE's
// own "we could not update your cart" line. Two things stand between a key and the customer:
//
// 1. the city and street wording is market-specific — Bahrain says "Town/City", the UAE says
//    "Flat number/Building/Street address";
// 2. a key with no dictionary entry must never be shown. The Errors block renders plain strings as
//    they are, so an untranslated `error_password_length` would reach the customer as-is.
const MARKET_KEYS = {
    error_address_street_empty: {
        Bahrain: 'error_bhr_address_empty',
        Kuwait: 'error_kwt_address_empty',
        Qatar: 'error_qar_address_empty',
        'Saudi Arabia': 'error_sar_address_empty',
        'United Arab Emirates': 'error_uae_address_empty',
        default: 'error_address_empty'
    },
    error_city_empty: {
        Bahrain: 'error_bhr_city_empty',
        Kuwait: 'error_kwt_city_empty',
        Qatar: 'error_qar_city_empty',
        'Saudi Arabia': 'error_sar_city_empty',
        default: 'error_city_empty'
    }
}

// Told to the customer when the payment page sends a reloaded guest back to the form. FE-owned copy,
// so it ships with its fallback the same way the rest of the new strings do (Phase 9 dictionary list).
export const ENTER_PASSWORD_AGAIN_KEY = 'checkout_enter_password_again'

// Last resort when Place order fails and the API named no field — a gateway timeout on createAccount,
// for instance. It does not promise the order was not taken, because a timeout cannot tell us that.
export const PLACE_ORDER_FAILED_KEY = 'place_order_failed'

const FALLBACK_COPY = {
    [ENTER_PASSWORD_AGAIN_KEY]: 'For your security we do not keep your password. Please enter it again to finish your order.',
    [PLACE_ORDER_FAILED_KEY]: 'We could not complete your order. Please try again, or contact us with the reference below.'
}

// Mirrors commitErrorText: the Errors block prints plain strings, so callers need display text.
export const placeOrderFailedText = () => translateKey(PLACE_ORDER_FAILED_KEY, { defaultDisplay: FALLBACK_COPY[PLACE_ORDER_FAILED_KEY] })

// The country arrives as whatever the form or the session holds — an English name from
// `detail_page.country`, a native name while the page is in Arabic. `uaeAreaCountries` maps both onto
// the one English name the table above is keyed by.
export const marketErrorKey = (key, countryValue) => {
    const perMarket = MARKET_KEYS[key]

    if (!perMarket) {
        return key
    }

    return perMarket[get(uaeAreaCountries(countryValue), 'name', '')] || perMarket.default
}

const displayText = (entry, countryValue) => {
    if (typeof entry !== 'string' || entry.trim() === '') {
        return null
    }

    const key = marketErrorKey(entry.trim(), countryValue)
    const translated = translateKey(key, { defaultDisplay: FALLBACK_COPY[key] })

    if (translated !== key) {
        return translated
    }

    // No dictionary entry for it. Text with a space in it is already a sentence and safe to show; a
    // bare key is not.
    return /\s/.test(entry) ? entry : null
}

const reported = keys => (Array.isArray(keys) ? keys : []).filter(entry => typeof entry === 'string' && entry.trim() !== '')

/**
 * Turns a Message_v2 list into text the Errors block can render. Empty in, empty out — and empty as
 * well when nothing in the list can be shown, so the caller decides whether silence is acceptable.
 *
 * @param {string[]} keys        Message_v2 from the API, or any list of dictionary keys.
 * @param {string} countryValue  Shipping country, for the market-specific wording.
 */
export const checkoutErrorMessages = (keys, countryValue) =>
    reported(keys).reduce((acc, entry) => {
        const text = displayText(entry, countryValue)

        if (text !== null && !acc.includes(text)) {
            acc.push(text)
        }

        return acc
    }, [])

/**
 * The same, for a form that has to answer for something the customer just did: when the API reported a
 * problem we have no copy for, saying nothing would look like the button did nothing. Still empty when
 * the API reported no problem at all.
 */
export const checkoutErrorText = (keys, countryValue, fallbackText) => {
    if (reported(keys).length === 0) {
        return []
    }

    const messages = checkoutErrorMessages(keys, countryValue)

    return messages.length > 0 ? messages : [fallbackText]
}
